import { Scenarios, Moves } from '@gql/base-types';
import { getTimingMove, hasRideMove, isConciergePickupScenario, isConciergeReturnScenario, type Scenario as SdkScenario } from '@hopdrive/sdk-appointments';
import { getMoveDisplayDate, getMoveDisplayTime } from './moveDisplayDate';

export interface DisplayRange {
  startTime: string;
  endTime: string;
}

/**
 * Get the visual display range for a scenario on the timeline.
 *
 * This calculates the horizontal position of scenario blocks for UI rendering.
 * It differs from the algorithm's getScenarioTimeRange (in timeRangeUtils.ts) which
 * calculates actual occupied time for conflict detection.
 *
 * Key differences from algorithm time range:
 * - Uses MoveTile visual width formulas (subtracting inspections, adding wait times)
 * - Uses pickup_started as anchor for in-progress moves
 * - Filters moves by display date
 * - Returns ISO strings (for getGapBetweenTimes positioning)
 *
 * @param scenario - The scenario to calculate display range for
 * @param date - The selected date (YYYY-MM-DD) to filter moves by
 * @param timezoneOverride - Timezone for date calculations
 * @returns Display range with startTime/endTime ISO strings, or null if no valid moves
 */
export function getScenarioDisplayRange(
  scenario: Scenarios,
  date: string,
  timezoneOverride: string
): DisplayRange | null {
  // Get all valid moves from the scenario
  const allMoves = scenario.moves
    ?.map((link) => link.move)
    .filter((move): move is Moves => !!move) || [];

  // Check if any move's display date matches the selected date
  const hasMatchingMove = allMoves.some((move) => {
    const displayDate = getMoveDisplayDate(move, timezoneOverride);
    return displayDate === date;
  });

  if (!hasMatchingMove || allMoves.length === 0) return null;

  // Use all moves for lane data lookups (ride moves need to be available for visual width)
  const moves = allMoves;

  // Helper to get the appropriate time for a move
  const getMoveStartTime = (move: Moves) => {
    // Use activity timestamp if move was worked on this date, otherwise use ready_by
    return getMoveDisplayTime(move, date, timezoneOverride);
  };

  // Check if this scenario has a real ride move (Standard tier has these, Lite doesn't)
  const hasRealRideMove = hasRideMove(scenario);

  // Find the drive and ride moves
  const driveMove = moves.find((m) => m.move_type === 'drive');
  const rideMove = moves.find((m) => m.move_type === 'ride' && m.active !== 0);

  // Determine the appointment time based on scenario type
  // For Standard tier (has ride move): always use drive move's ready_by as the appointment anchor
  // For Lite tier: use the timing move's pickup_started or ready_by
  let appointmentTime: number;

  if (hasRealRideMove && driveMove?.ready_by) {
    // Standard tier: drive move has the actual appointment time
    appointmentTime = new Date(driveMove.pickup_started || driveMove.ready_by).getTime();
  } else {
    // Lite tier / no ride move: use the timing move
    const timingMove = getTimingMove(scenario as unknown as SdkScenario);
    const appointmentTimeStr = timingMove?.pickup_started || timingMove?.ready_by;
    if (!appointmentTimeStr) return null;
    appointmentTime = new Date(appointmentTimeStr).getTime();
  }

  let startTime = appointmentTime;

  // For Standard tier concierge-pu WITH a ride move:
  // Position based on the VISUAL width of the ride tile
  // MoveTile calculates width as: (duration - pickup_inspection - delivery_inspection + return_wait) / 60
  if (isConciergePickupScenario(scenario.scenario_key) && hasRealRideMove && rideMove && driveMove?.ready_by) {
    const actualAppointmentTime = new Date(driveMove.pickup_started || driveMove.ready_by).getTime();

    const rideLane = rideMove.lane;
    const rideVisualWidthMin = rideLane
      ? ((rideLane.duration_sec || 0) -
         (rideLane.pickup_inspection_sec || 0) -
         (rideLane.delivery_inspection_sec || 0) +
         (rideLane.return_ride_wait_sec || 0)) / 60
      : 0;
    const rideVisualWidthMs = rideVisualWidthMin * 60 * 1000;

    startTime = actualAppointmentTime - rideVisualWidthMs;
  }

  // For Standard tier concierge-ret WITH a ride move:
  // Drive block ends at appointment, ride block starts at appointment
  if (isConciergeReturnScenario(scenario.scenario_key) && hasRealRideMove && driveMove?.ready_by) {
    const actualAppointmentTime = new Date(driveMove.pickup_started || driveMove.ready_by).getTime();

    const driveLane = driveMove.lane;
    const driveVisualWidthMin = driveLane
      ? (driveLane.duration_sec || 0) / 60
      : 0;
    const driveVisualWidthMs = driveVisualWidthMin * 60 * 1000;

    startTime = actualAppointmentTime - driveVisualWidthMs;
  }

  // For concierge-ret WITHOUT a ride move (Lite tier):
  // Block start = appointment time - travel duration
  if (isConciergeReturnScenario(scenario.scenario_key) && !hasRealRideMove) {
    const travelToCustomerSec = driveMove?.lane?.inverse?.duration_sec || driveMove?.lane?.duration_sec || 0;
    startTime = appointmentTime - (travelToCustomerSec * 1000);

    return {
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(appointmentTime).toISOString(),
    };
  }

  // For concierge-loaner-pu:
  // Scenario START = appointmentTime - parentMoveDuration
  if (scenario.scenario_key === 'concierge-loaner-pu') {
    const parentMove = moves.find((m) => m.parent_move_id == null);
    const parentDurationSec = parentMove?.lane?.duration_sec || 0;
    startTime = appointmentTime - (parentDurationSec * 1000);
  }

  // For concierge-loaner-ret:
  // Scenario START = appointmentTime - childMoveDuration
  if (scenario.scenario_key === 'concierge-loaner-ret') {
    const childMove = moves.find((m) => m.parent_move_id != null);
    const childDurationSec = childMove?.lane?.duration_sec || 0;
    startTime = appointmentTime - (childDurationSec * 1000);
  }

  // For other scenarios: Find latest delivery time (or pickup + duration)
  const endTime = moves.reduce((latest, move) => {
    let moveEndTime: number;
    if (move.delivery_time) {
      moveEndTime = new Date(move.delivery_time).getTime();
    } else {
      const pickupTimeStr = getMoveStartTime(move);
      if (!pickupTimeStr) return latest;
      const pickupTime = new Date(pickupTimeStr).getTime();

      const durationSec = move.lane?.duration_sec || 2 * 60 * 60;
      const durationMs = durationSec * 1000;
      moveEndTime = pickupTime + durationMs;
    }
    return moveEndTime > latest ? moveEndTime : latest;
  }, 0);

  return {
    startTime: new Date(startTime).toISOString(),
    endTime: new Date(endTime).toISOString(),
  };
}
